// Search and browse-by-system over the sample content (EXP 01). The content
// set is small, so it is searched in the page with no search service.
//
// The links search builds and the page's router share one format, set here:
// #/<section>/<id>. routeFor is the router's reading of an address, so a test
// can follow any link through it.

export type Page =
  | 'home'
  | 'search'
  | 'system'
  | 'entry'
  | 'calendar'
  | 'teleos'
  | 'tools'
  | 'guides'
  | 'support'
  | 'publish'
  | 'not-found';

export function routeFor(hash: string): { page: Page; id: string } {
  const path = decodeURIComponent(hash.replace(/^#/, '')) || '/';
  const [, section = '', id = ''] = path.split('/');
  switch (section) {
    case '':
      return { page: 'home', id: '' };
    case 'search':
    case 'system':
      return { page: section, id };
    case 'calendar':
      return { page: id ? 'entry' : 'calendar', id };
    case 'tools':
      return { page: id === 'teleos' ? 'teleos' : 'tools', id };
    case 'guides':
    case 'training':
      return { page: 'guides', id };
    case 'support':
    case 'publish':
      return { page: section, id: '' };
    default:
      return { page: 'not-found', id: '' };
  }
}

// A tool in the directory. The directory page scrolls to that tool.
export const toolHref = (id: string) => `#/tools/${encodeURIComponent(id)}`;

export interface SearchItem {
  kind: 'tool' | 'guide' | 'training' | 'change' | 'page';
  title: string;
  system?: string;
  text: string;
  href: string;
}

interface Content {
  tools: Array<{ id: string; name: string; system: string; description: string; overview?: string }>;
  guides: Array<{ id: string; title: string; system: string; summary: string; steps: Array<{ text: string }> }>;
  training: Array<{ id: string; title: string; system: string }>;
  teleos: { title: string; overview: string; why: string };
  areas: Array<{ id: string; title: string; href: string; summary: string }>;
}

interface Change {
  id: string;
  title: string;
  systems: string[];
  impact: string;
  reference?: string;
}

export function buildIndex(content: Content, changes: Change[] = []): SearchItem[] {
  return [
    ...content.areas.map((a) => ({ kind: 'page' as const, title: a.title, text: a.summary, href: a.href })),
    {
      kind: 'page' as const,
      title: content.teleos.title,
      system: 'Teleos',
      text: `${content.teleos.overview} ${content.teleos.why}`,
      href: '#/tools/teleos',
    },
    ...content.tools.map((t) => ({
      kind: 'tool' as const,
      title: t.name,
      system: t.system,
      text: t.description,
      href: t.overview ?? toolHref(t.id),
    })),
    ...content.guides.map((g) => ({
      kind: 'guide' as const,
      title: g.title,
      system: g.system,
      text: `${g.summary} ${g.steps.map((s) => s.text).join(' ')}`,
      href: `#/guides/${g.id}`,
    })),
    ...content.training.map((t) => ({ kind: 'training' as const, title: t.title, system: t.system, text: t.title, href: '#/training' })),
    ...changes.map((c) => ({
      kind: 'change' as const,
      title: c.title,
      system: c.systems.join(', '),
      text: `${c.reference ?? ''} ${c.impact}`,
      href: `#/calendar/${c.id}`,
    })),
  ];
}

const words = (s: string) =>
  s
    .toLowerCase()
    .split(/[^\p{L}\p{N}-]+/u)
    .filter((w) => w.length > 1);

// Every word in the query has to appear. Title matches rank first.
export function search(index: SearchItem[], query: string): SearchItem[] {
  const terms = words(query);
  if (terms.length === 0) return [];
  return index
    .map((item) => {
      const title = item.title.toLowerCase();
      const all = `${title} ${(item.system ?? '').toLowerCase()} ${item.text.toLowerCase()}`;
      if (!terms.every((t) => all.includes(t))) return null;
      const score = terms.reduce((n, t) => n + (title.includes(t) ? 3 : 1), 0);
      return { item, score };
    })
    .filter((x): x is { item: SearchItem; score: number } => x !== null)
    .sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title))
    .map((x) => x.item);
}

export function bySystem(index: SearchItem[], system: string): SearchItem[] {
  const s = system.toLowerCase();
  return index.filter((i) => (i.system ?? '').toLowerCase().split(/,\s*/).includes(s));
}
